import React, { Component } from 'react';
import { ErrorContext } from '../contexts/ErrorContext';

/**
 * ErrorBoundary component to catch React component rendering errors
 * Provides fallback UI for critical errors
 * Logs errors to console for debugging
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    /**
     * Static method called when an error is thrown in a child component
     * Updates state to trigger fallback UI
     */
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error
        };
    }

    /**
     * Lifecycle method called after an error is caught
     * Logs error details and integrates with Error Context
     */
    componentDidCatch(error, errorInfo) {
        // Log error to console for debugging
        console.error('ErrorBoundary caught an error:', {
            error,
            errorInfo,
            componentStack: errorInfo.componentStack,
            timestamp: new Date().toISOString()
        });

        // Store error info in state
        this.setState({
            errorInfo
        });

        // Note: We do NOT show runtime errors in the global error modal
        // Instead, we show a fallback UI below and log to console
    }

    /**
     * Reset error state to attempt recovery
     */
    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });

        // Clear error from context if available
        if (this.context && this.context.clearError) {
            this.context.clearError();
        }
    };

    render() {
        if (this.state.hasError) {
            // Render fallback UI for critical errors with animation
            return (
                <div style={styles.container}>
                    <style>{keyframes}</style>
                    <div style={styles.card}>
                        {/* Animated Robot/Error Icon */}
                        <div style={styles.iconContainer}>
                            <div style={styles.robotContainer}>
                                {/* Robot Head */}
                                <div style={styles.robotHead}>
                                    <div style={styles.robotEye}>×</div>
                                    <div style={{ ...styles.robotEye, left: '60%' }}>×</div>
                                </div>
                                {/* Robot Body */}
                                <div style={styles.robotBody}>
                                    <div style={styles.errorBadge}>!</div>
                                </div>
                                {/* Gears */}
                                <div style={styles.gear1}>⚙</div>
                                <div style={styles.gear2}>⚙</div>
                            </div>
                        </div>

                        {/* Animated "Oops!" text */}
                        <h1 style={styles.oopsText}>Oops!</h1>

                        <h2 style={styles.title}>Something went wrong</h2>
                        <p style={styles.message}>
                            The application encountered an unexpected error. Don't worry, we're on it!
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Error Details (Development Only)</summary>
                                <pre style={styles.errorText}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        <div style={styles.buttonContainer}>
                            <button
                                onClick={this.handleReset}
                                style={styles.button}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = styles.buttonHover.backgroundColor;
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = styles.button.backgroundColor;
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                                }}
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                style={styles.secondaryButton}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = styles.secondaryButtonHover.backgroundColor;
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = styles.secondaryButton.backgroundColor;
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
                                }}
                            >
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // Render children normally when no error
        return this.props.children;
    }
}

// Set context type to access Error Context
ErrorBoundary.contextType = ErrorContext;

// CSS Keyframes for animations
const keyframes = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }

  @keyframes shake {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-5deg); }
    75% { transform: rotate(5deg); }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

// Inline styles for fallback UI
const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden'
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        padding: '50px 40px',
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center',
        animation: 'fadeInUp 0.6s ease-out',
        position: 'relative',
        zIndex: 1
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '30px',
        animation: 'float 3s ease-in-out infinite'
    },
    robotContainer: {
        position: 'relative',
        width: '150px',
        height: '150px'
    },
    robotHead: {
        width: '80px',
        height: '80px',
        backgroundColor: '#FFC107',
        borderRadius: '15px',
        position: 'absolute',
        top: '0',
        left: '50%',
        transform: 'translateX(-50%)',
        animation: 'shake 2s ease-in-out infinite',
        boxShadow: '0 8px 20px rgba(255, 193, 7, 0.4)'
    },
    robotEye: {
        position: 'absolute',
        top: '25px',
        left: '20%',
        width: '15px',
        height: '15px',
        backgroundColor: '#2c3e50',
        borderRadius: '50%',
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#e74c3c',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1'
    },
    robotBody: {
        width: '100px',
        height: '70px',
        backgroundColor: '#FFC107',
        borderRadius: '10px',
        position: 'absolute',
        bottom: '0',
        left: '50%',
        transform: 'translateX(-50%)',
        boxShadow: '0 8px 20px rgba(255, 193, 7, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    errorBadge: {
        width: '40px',
        height: '40px',
        backgroundColor: '#e74c3c',
        borderRadius: '50%',
        color: '#fff',
        fontSize: '28px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 1.5s ease-in-out infinite'
    },
    gear1: {
        position: 'absolute',
        top: '20px',
        left: '-10px',
        fontSize: '30px',
        color: '#95a5a6',
        animation: 'spin 3s linear infinite',
        opacity: 0.7
    },
    gear2: {
        position: 'absolute',
        bottom: '10px',
        right: '-10px',
        fontSize: '25px',
        color: '#95a5a6',
        animation: 'spin 4s linear infinite reverse',
        opacity: 0.7
    },
    oopsText: {
        fontSize: '48px',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: '10px',
        marginTop: '0',
        animation: 'fadeInUp 0.8s ease-out 0.2s both'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#2c3e50',
        marginBottom: '15px',
        marginTop: '0',
        animation: 'fadeInUp 1s ease-out 0.4s both'
    },
    message: {
        fontSize: '18px',
        color: '#7f8c8d',
        marginBottom: '30px',
        lineHeight: '1.6',
        animation: 'fadeInUp 1.2s ease-out 0.6s both'
    },
    details: {
        textAlign: 'left',
        marginBottom: '24px',
        backgroundColor: '#f9fafb',
        padding: '16px',
        borderRadius: '4px',
        border: '1px solid #e5e7eb'
    },
    summary: {
        cursor: 'pointer',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '8px'
    },
    errorText: {
        fontSize: '12px',
        color: '#ef4444',
        overflow: 'auto',
        maxHeight: '200px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    },
    buttonContainer: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap'
    },
    button: {
        backgroundColor: '#667eea',
        color: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        padding: '14px 32px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        outline: 'none',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
        animation: 'fadeInUp 1.4s ease-out 0.8s both'
    },
    buttonHover: {
        backgroundColor: '#5568d3'
    },
    secondaryButton: {
        backgroundColor: '#95a5a6',
        color: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        padding: '14px 32px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        outline: 'none',
        boxShadow: '0 4px 12px rgba(149, 165, 166, 0.3)',
        animation: 'fadeInUp 1.4s ease-out 0.8s both'
    },
    secondaryButtonHover: {
        backgroundColor: '#7f8c8d'
    }
};

export default ErrorBoundary;
